import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { resetForm } from '@/reducers/CurrentForm';
import { selectIsRedirectImmediately, selectOpenBackButtonErrorModal, setOpenBackButtonErrorModal, setRedirectImmediately } from '@/reducers/currentPage';
import { resetState as resetLoanApplication } from '@/reducers/LoanApplication';
import { resetState as resetVmoV2 } from '@/reducers/VmoV2';
import { normalizeUrl } from '@/utils/urlUtils';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DefaultModal } from './components/Modals/DefaultModal/DefaultModal';

interface ProtectedRouteProps {
  element: React.ReactElement;
  isProtected?: boolean;
}

const ProtectedRoute: FC<ProtectedRouteProps> = ({ element }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isRedirectImmediately = useAppSelector(selectIsRedirectImmediately);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const openBackButtonErrorModal = useAppSelector(selectOpenBackButtonErrorModal);

  // Preserve query parameters during navigation
  const preserveQueryParams = (path: string) => {
    const searchParams = new URLSearchParams(location.search);
    const queryString = searchParams.toString();
    return queryString ? `${path}?${queryString}` : path;
  };

  // navigation guard effect
  useEffect(() => {
    const handleBeforeUnload = () => {
      // handle page refresh/close, but cannot tell yet, only when the page is loaded
      const currentUrl = normalizeUrl(window.location.href);
      sessionStorage.setItem('lastVisitedUrlBeforeUnload', currentUrl);
    };

    // handle browser back/forward
    const handlePopState = () => {
      const lastVisitedUrl = sessionStorage.getItem('previousVisitedUrl');
      if (lastVisitedUrl) {
        dispatch(setOpenBackButtonErrorModal(true));
        const normalizedUrl = normalizeUrl(lastVisitedUrl);
        window.history.pushState(null, '', normalizedUrl);
        const url = new URL(normalizedUrl);
        sessionStorage.setItem('lastVisitedUrl', normalizedUrl);
        navigate(url.pathname + url.search);
      }
    };

    const handleLoad = () => {
      handleUrlChange();
    };

    const handleUrlChange = () => {
      const previousUrl = normalizeUrl(sessionStorage.getItem('lastVisitedUrlBeforeUnload'));
      const currentUrl = normalizeUrl(window.location.href);
      // detect url change that is not a page refresh
      if (previousUrl === '') {
        // if the lastVisitedUrlBeforeUnload is not set, then set it
        sessionStorage.setItem('lastVisitedUrlBeforeUnload', currentUrl);
      } else if (previousUrl !== currentUrl) {
        // if the url is different from the lastVisitedUrlBeforeUnload, then redirect
        setRedirectImmediately(true);
        setIsRedirecting(true);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('load', handleLoad);

    return () => {};
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRedirect = async () => {
    if (isRedirectImmediately || isRedirecting) {
      sessionStorage.clear();

      // reset slices
      dispatch(resetVmoV2());
      dispatch(resetForm());
      dispatch(resetLoanApplication());
      dispatch({ type: 'CurrentPage/resetState', payload: undefined });
      dispatch(setRedirectImmediately(false));

      // redirect and refresh with preserved query parameters
      window.location.href = preserveQueryParams('/get-started');
    }
  };
  useEffect(() => {
    handleRedirect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    handleRedirect();
  }, [isRedirecting]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleModalClose = () => {
    dispatch(setOpenBackButtonErrorModal(false));
  };

  return (
    <>
      {element}
      {openBackButtonErrorModal && (
        <DefaultModal
          openModal={openBackButtonErrorModal}
          setOpenModal={handleModalClose}
          isErrorModal={false}
          infoModal={{
            body: 'To ensure your information is saved correctly, please use the in-app navigation buttons.',
            button: 'Ok',
          }}
        />
      )}
    </>
  );
};

export default ProtectedRoute;
